/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-04 16:02:40                               *
 * @LastEditors           : Hamza Damene                                      *
 * @LastEditDate          : 2025-03-19 15:00:00                               *
 *****************************************************************************/

/* SUMMARY
 * Imports
 * Components
 * Styles
 * Informations: workorder informations tab
 * handleChange: handle input change
 * handleSelectChange: handle select change
 * handleDateRangeChange: handle date range change
 * onSubmit: on submit event
 */

/* Imports */
import { Card, Typography } from "@material-tailwind/react";
import { t } from "i18next";
import { useWorkorderInformation } from "../../../../services/../hooks/useWorkorderInformation";

/***/

/* Components */
import { GeneralSection } from "./components/GeneralSection";
import { ScheduleSection } from "./components/ScheduleSection";
import { TimeSection } from "./components/TimeSection";
import { OnCallSection } from "./components/OnCallSection";
import { NotesSection } from "./components/NotesSection";
import { AdditionalInfosSection } from "./components/AdditionalInfosSection";
import { OriginTypeSection } from "./components/OriginTypeSection";
/***/

/* Styles */
import "./Informations.scss";
import EditCreateDrawer from "../../../../components/edit-create-drawer/EditCreateDrawer";
import WorkorderForm, { WorkorderFormValidator } from "../../../../components/data/workorder/WorkorderForm";
import { compInfoFieldName } from "../../../../components/data/compinfos/compinfo-errors";
import { getWorkorderTimeError } from "../../../../components/data/workorder/workorder-time-rules";
/***/

/**
 * Composant d'affichage et de modification des informations générales d'un bon de travail
 * @param data - Données du workorder à afficher/modifier
 * @returns Composant React pour gérer les informations du workorder
 */
export function Informations({ data }: { data: EasyTech.Workorder }) {

  // Hook pour gérer les informations du workorder
  const informationHook = useWorkorderInformation(data.id);
  const {
    canEditAnyField,
    saveWorkorderChanges
  } = informationHook;

  const workorderFieldNames = {
    customer: t("common:customer"),
    contacts: t("common:contacts"),
    address: t("common:address"),
    // Nomme les champs obligatoires restés vides, le bloc en porte une quinzaine
    compInfos: compInfoFieldName(t),
    schedule: t("workorder:schedule"),
    // The real dates are refused for a reason worth naming, so the toast carries it in place of the label
    time: (workorder: EasyTech.Workorder) => getWorkorderTimeError(workorder?.time, t) || t("workorder:time")
  };

  const handleWorkorderEdit = (value: EasyTech.Workorder): Promise<void> =>  {
    return saveWorkorderChanges(value, data);
  };

  return (
    <div className="workorder-information">
      <div className="flex justify-between items-center">
        <div className="information-header">
          <Typography variant="h4">{t("common:general")}</Typography>
        </div>

        {canEditAnyField && (
          <div className="flex gap-2">
            <EditCreateDrawer<EasyTech.Workorder> FormComponent={WorkorderForm}
                                                  data={data}
                                                  isNotCreatable
                                                  onSubmit={handleWorkorderEdit}
                                                  entityName="intervention"
                                                  editButtonText={t("common:edit")}
                                                  validator={WorkorderFormValidator}
                                                  fieldNames={workorderFieldNames} />
          </div>
        )}
      </div>

      <Card className="information-content">
        {/* Section des informations générales */}
        <GeneralSection 
          workorder={data} 
        />
        
        {/* Section de planification */}
        <ScheduleSection 
          workorder={data} 
          informationHook={informationHook} 
        />
        
        {/* Section du temps réel */}
        <TimeSection 
          workorder={data} 
          informationHook={informationHook} 
        />

        
        {/* Section origine et type */}
        <OriginTypeSection
          workorder={data} 
        />
        
        {/* Section d'astreinte */}
        <OnCallSection 
          workorder={data} 
        />

      </Card>

      {/* Section des notes : note générale, note technicien et note planificateur */}
      <NotesSection workorder={data} />

      {/* Section des informations complémentaires */}
      <AdditionalInfosSection workorder={data} />
    </div>
  );
}
/***/
